import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { flatMap, map, take } from 'rxjs/operators';

import {
  Stats,
  StatGroupBy,
  StatGroupByRaw,
  StatsRaw,
  GroupBy,
} from './stats.interface';
import { ApiResourceService } from '../api-resource/api-resource.service';
import { AuthenticationService } from '../authentication/authentication.service';
import { Experience } from '../presubscription-resource/experience.enum';

function translateLabel(groupBy: GroupBy, label: string) {
  switch (groupBy) {
    case GroupBy.Experience:
      return mapExperience(label as Experience);
    case GroupBy.AgeCategory:
      return label === 'other' ? 'Autre' : label;
    case GroupBy.Status:
      switch (label) {
        case 'waiting':
          return 'En attente';
        case 'cancelled':
          return 'Annulé';
        case 'assigned':
          return 'Assigné';
        case 'waiting_list':
          return "Liste d'attente";
        default:
          return 'Autre';
      }
    default:
      return label;
  }
}

@Injectable()
export class StatsResourceService {
  private http = inject(HttpClient);
  private readonly apiResourceService = inject(ApiResourceService);
  private readonly authService = inject(AuthenticationService);

  public getCountBy(params: {
    group_by: GroupBy[];
    season_id?: number;
  }): Observable<StatGroupBy[]> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) => {
        return this.http.get<StatGroupByRaw[]>(
          this.apiResourceService.getApiUrl(
            `/stats/season/${params.season_id}`,
          ),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
            params: {
              groupBy: params.group_by.join('|'),
            },
          },
        );
      }),
      map((rawLines) =>
        rawLines.map((rawLine) => ({
          ...rawLine,
          count: Number(rawLine.count),
        })),
      ),
      map((rawLines) =>
        rawLines.map((rawLine) => ({
          ...rawLine,
          label1: translateLabel(params.group_by[0], rawLine.label1),
          ...(rawLine.label2
            ? { label2: translateLabel(params.group_by[1], rawLine.label2) }
            : {}),
        })),
      ),
    );
  }

  public getCountByCityForSeason(params: {
    season_id?: number;
  }): Observable<Stats> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) => {
        return this.http.get<StatsRaw>(
          this.apiResourceService.getApiUrl(
            `/stats/season/${params.season_id}/cities`,
          ),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        );
      }),
      map((rawLines) =>
        rawLines.map((rawLine) => ({
          ...rawLine,
          count: Number(rawLine.count),
        })),
      ),
    );
  }

  public getCountByExperienceForSeason(params: {
    season_id?: number;
  }): Observable<Stats> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) => {
        return this.http.get<StatsRaw>(
          this.apiResourceService.getApiUrl(
            `/stats/season/${params.season_id}/experience`,
          ),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        );
      }),
      map((rawLines) =>
        rawLines.map((rawLine) => ({
          label: mapExperience(rawLine.label as Experience),
          count: Number(rawLine.count),
        })),
      ),
    );
  }

  public getCountByAgeCategoryForSeason(params: {
    season_id?: number;
  }): Observable<Stats> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) => {
        return this.http.get<StatsRaw>(
          this.apiResourceService.getApiUrl(
            `/stats/season/${params.season_id}/age-categories`,
          ),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        );
      }),
      map((rawLines) =>
        rawLines.map((rawLine) => ({
          label: rawLine.label,
          count: Number(rawLine.count),
        })),
      ),
    );
  }
}

function mapExperience(experience: Experience): string {
  switch (experience) {
    case Experience.previousMember:
      return 'Ancien';
    case Experience.previousMemberOfOtherClub:
      return 'Autre club';
    default:
      return 'Nouveau';
  }
}
